import React, { useState, useEffect } from "react";

const BLANK_FORM = {
  hallName: "",
  sponsorName: "",
  donorName: "",
  entryPassCount: 100,
  winnerCount: 1,
  prizeDetails: "",
  specialMessage: "",
  instructions: "",
};

// Handles two cases now:
//   1. event === null  -> organiser has nothing yet, show a creation form
//   2. event exists     -> show read-only view with an Edit toggle (as before)
// This replaces the old version, which assumed `event` always existed
// (it was seeded with fake data by the dashboard before this rewrite).

const ReunionCreateEvent = ({ event, onCreate, onUpdate }) => {
  const isNew = !event;
  const [editing, setEditing] = useState(isNew);
  const [form, setForm] = useState(event ? { ...event } : { ...BLANK_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) setForm({ ...event });
  }, [event]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    if (isNew) {
      const created = await onCreate(form);
      if (created) setEditing(false);
    } else {
      const updated = await onUpdate(form);
      if (updated) setEditing(false);
    }

    setSaving(false);
  };

  return (
    <div className="space-y-3">
      {/* Header */}

      <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-black">
            {isNew ? "🆕 Create Your Reunion" : "⚙️ Reunion Display Settings"}
          </h2>

          <p className="text-emerald-200 text-sm mt-1">
            {isNew
              ? "Set these up once — you can edit them any time before going live."
              : "Configure your Reunion before going live."}
          </p>
        </div>

        <div className="flex gap-3">
          {!isNew && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 font-semibold"
            >
              ✏️ Edit
            </button>
          )}

          {(isNew || editing) && (
            <button
              onClick={handleSave}
              disabled={saving || !form.hallName}
              className="px-5 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 font-semibold disabled:opacity-40"
            >
              {saving ? "Saving..." : isNew ? "🚀 Create Reunion" : "💾 Save"}
            </button>
          )}
        </div>
      </div>

      {/* Card */}

      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-6">
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Subscription — only shown once the event exists; it's server-generated, never client-set */}
          {!isNew && (
            <div>
              <label className="block text-xs text-emerald-300 mb-2">
                Entry Pass / Session ID
              </label>

              <input
                disabled
                value={form.subscriptionId}
                className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 font-mono tracking-wider"
              />
            </div>
          )}

          {/* Hall */}

          <div>
            <label className="block text-xs text-emerald-300 mb-2">
              Hall Name
            </label>

            <input
              disabled={!isNew && !editing}
              name="hallName"
              value={form.hallName}
              onChange={handleChange}
              placeholder="e.g. Think5Whys Annual Meetup"
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3"
            />
          </div>

          {/* Sponsor */}

          <div>
            <label className="block text-xs text-emerald-300 mb-2">
              Sponsor
            </label>

            <input
              disabled={!isNew && !editing}
              name="sponsorName"
              value={form.sponsorName}
              onChange={handleChange}
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3"
            />
          </div>

          {/* Donor */}

          <div>
            <label className="block text-xs text-emerald-300 mb-2">Donor</label>

            <input
              disabled={!isNew && !editing}
              name="donorName"
              value={form.donorName}
              onChange={handleChange}
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3"
            />
          </div>

          {/* Entry / max participants */}

          <div>
            <label className="block text-xs text-emerald-300 mb-2">
              Max Participants
            </label>

            <input
              type="number"
              min={1}
              disabled={!isNew && !editing}
              name="entryPassCount"
              value={form.entryPassCount}
              onChange={handleChange}
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3"
            />
          </div>

          {/* Winners */}

          <div>
            <label className="block text-xs text-emerald-300 mb-2">
              Number of Winners
            </label>

            <select
              disabled={!isNew && !editing}
              name="winnerCount"
              value={form.winnerCount}
              onChange={handleChange}
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-1"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>

          {/* Prize */}

          <div className="lg:col-span-2">
            <label className="block text-xs text-emerald-300 mb-2">
              Prize Details
            </label>

            <input
              disabled={!isNew && !editing}
              name="prizeDetails"
              value={form.prizeDetails}
              onChange={handleChange}
              placeholder="🥇 ₹5000   🥈 ₹3000   🥉 ₹1000"
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3"
            />
          </div>

          {/* Message */}

          <div className="lg:col-span-2">
            <label className="block text-xs text-emerald-300 mb-2">
              Special Message
            </label>

            <textarea
              style={{height: "100px"}}
              disabled={!isNew && !editing}
              name="specialMessage"
              value={form.specialMessage}
              onChange={handleChange}
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 resize-none"
            />
          </div>

          {/* Instructions */}

          <div className="lg:col-span-2">
            <label className="block text-xs text-emerald-300 mb-2">
              Instructions
            </label>

            <textarea
              style={{height: "100px"}}
              disabled={!isNew && !editing}
              name="instructions"
              value={form.instructions}
              onChange={handleChange}
              className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 resize-none"
            />
          </div>
        </div>
      </div>

      {/* Status — only once the event exists */}
      {!isNew && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-5">
            <div className="text-sm text-emerald-300 mb-2">Current Status</div>

            <div className="text-2xl font-black">{form.status}</div>
          </div>

          <div className="rounded-2xl bg-purple-500/10 border border-purple-500/20 p-5">
            <div className="text-sm text-purple-300 mb-2">Live Event</div>

            <div className="text-2xl font-black">
              {form.isLive ? "🟢 LIVE" : "🟡 NOT LIVE"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReunionCreateEvent;
